import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLine, useAdminLoad } from '../aiAccess/shared';
import type { Batch, EnrichmentClient } from './api';
import { BATCH_STATES, KINDS, countsText, shortDateTime } from './format';
import { Badge } from './ui';

/** While a batch runs (here or in another admin's browser), the list loads again this often. */
export const UPLOADS_POLL_MS = 15_000;

const TH = 'border-b border-[#E5E7EB] dark:border-[#30363d] bg-[#F1F2F5] dark:bg-[#161b22] px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-[.05em] text-[#5C6B85] dark:text-[#8b949e]';
const TD = 'border-b border-[#E5E7EB] dark:border-[#30363d] px-4 py-3.5 align-top text-sm text-[#0B1B3D] dark:text-[#e6edf3]';

/** found / not found / failed / skipped of a batch's rows, as a thin bar. */
export function BatchBar({ batch }: { readonly batch: Batch }) {
  const { t } = useTranslation();
  const counts = batch.counts ?? {};
  const rows = Math.max(batch.row_count ?? 0, 1);
  const done = (counts.found ?? 0) + (counts.not_found ?? 0) + (counts.failed ?? 0) + (counts.skipped ?? 0);
  const segments = [
    { key: 'found', colour: 'bg-[#10B981]' },
    { key: 'not_found', colour: 'bg-[#0B1B3D] dark:bg-[#e6edf3]' },
    { key: 'failed', colour: 'bg-[#DC2626]' },
  ];
  return (
    <div
      role="progressbar"
      aria-label={t('{{file}} progress', { file: batch.file_name ?? '' })}
      aria-valuemin={0}
      aria-valuemax={batch.row_count ?? 0}
      aria-valuenow={done}
      className="mb-1.5 flex h-1.5 w-40 overflow-hidden rounded-full bg-[#F1F2F5] dark:bg-[#21262d]"
    >
      {segments.map((segment) => (
        <div key={segment.key} className={segment.colour} style={{ width: `${((counts[segment.key] ?? 0) / rows) * 100}%` }} />
      ))}
    </div>
  );
}

/**
 * The Uploads view: the tenant's list lookups and imports, newest first. It loads again when
 * `version` changes, and every UPLOADS_POLL_MS while it shows (`active`) and a batch is running.
 */
export function UploadsTable({ client, version, active }: {
  readonly client: EnrichmentClient | null;
  readonly version: number;
  readonly active: boolean;
}) {
  const { t } = useTranslation();
  const { data, loading, error, reload } = useAdminLoad<Batch[], EnrichmentClient>(client, (c, signal) => c.batches(undefined, signal));
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const firstVersion = useRef(version);
  useEffect(() => {
    if (version !== firstVersion.current) void reloadRef.current();
  }, [version]);
  const batches = Array.isArray(data) ? data : [];
  const running = batches.some((batch) => batch.state === 'running');
  useEffect(() => {
    if (!active || !running) return undefined;
    const timer = setInterval(() => void reloadRef.current(), UPLOADS_POLL_MS);
    return () => clearInterval(timer);
  }, [active, running]);

  return (
    <section aria-label={t('Uploads')} className="overflow-hidden rounded-xl border border-[#E5E7EB] dark:border-[#30363d] bg-white dark:bg-[#0d1117]">
      {error && <div className="px-4 pt-3"><ErrorLine message={error} /></div>}
      {!data && loading && <p className="m-0 px-4 py-8 text-center text-sm text-[#5C6B85] dark:text-[#8b949e]">{t('Loading…')}</p>}
      {data && batches.length === 0 && (
        <p className="m-0 px-4 py-8 text-center text-sm text-[#5C6B85] dark:text-[#8b949e]">
          {t('Nothing has been uploaded yet. Upload a list to look it up, or import answers enriched elsewhere.')}
        </p>
      )}
      {batches.length > 0 && (
        <div className="overflow-x-auto">
          <table aria-label={t('Uploads')} className="w-full min-w-[860px] border-collapse">
            <thead>
              <tr>
                <th className={TH}>{t('Started')}</th>
                <th className={TH}>{t('File')}</th>
                <th className={TH}>{t('Kind')}</th>
                <th className={TH}>{t('By')}</th>
                <th className={`${TH} !text-right`}>{t('Rows')}</th>
                <th className={TH}>{t('Outcome')}</th>
                <th className={TH}>{t('State')}</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch, index) => {
                const state = BATCH_STATES[batch.state ?? ''] ?? { label: batch.state || '—', tone: 'neutral' as const };
                return (
                  <tr key={batch.id ?? index}>
                    <td className={`${TD} whitespace-nowrap text-[#434A59] dark:text-[#c9d1d9]`} title={batch.finished_at ? t('Finished {{when}}', { when: shortDateTime(batch.finished_at) }) : undefined}>
                      {shortDateTime(batch.started_at)}
                    </td>
                    <td className={`${TD} font-semibold`}>{batch.file_name || '—'}</td>
                    <td className={TD}>{t(KINDS[batch.kind ?? ''] ?? batch.kind ?? '—')}</td>
                    <td className={`${TD} text-[#434A59] dark:text-[#c9d1d9]`}>{batch.submitted_by_name || '—'}</td>
                    <td className={`${TD} text-right`}>{batch.row_count?.toLocaleString() ?? '—'}</td>
                    <td className={`${TD} text-[13px]`}>
                      {batch.state === 'running' && <BatchBar batch={batch} />}
                      {countsText(batch.counts, t) || <span className="text-[#5C6B85] dark:text-[#8b949e]">—</span>}
                    </td>
                    <td className={TD}><Badge tone={state.tone}>{t(state.label)}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
